import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Eye } from "lucide-react";
import { AdminGuard } from "../../components/AdminGuard";
import { ConfirmAction } from "../../components/ConfirmAction";
import { Wordmark } from "../../components/Header";
import { usePageMeta } from "../../components/Common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: "border-crimson/50 text-crimson",
  review: "border-navy/40 text-navy",
  published: "bg-navy text-white border-navy",
  archived: "border-charcoal/30 text-charcoal/50",
};
const STATUS_LABELS = {
  draft: "Draft",
  review: "Ready for Review",
  published: "Published",
  archived: "Archived",
};

const Dashboard = () => {
  const [articles, setArticles] = useState(null);
  const navigate = useNavigate();

  usePageMeta("Editorial Dashboard — English for Leaders", undefined, "/admin", {
    noindex: true,
  });

  const load = () =>
    axios
      .get(`${API}/admin/articles`, { withCredentials: true })
      .then((r) => setArticles(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
        else toast.error("Could not load articles.");
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = (id, action, label) =>
    axios
      .post(`${API}/admin/articles/${id}/${action}`, {}, { withCredentials: true })
      .then(() => {
        toast.success(label);
        load();
      })
      .catch((e) =>
        toast.error(e?.response?.data?.detail || `Could not ${action} the article.`)
      );

  const duplicate = (id) =>
    axios
      .post(`${API}/admin/articles/${id}/duplicate`, {}, { withCredentials: true })
      .then((r) => {
        toast.success("Duplicated as a new draft.");
        load();
      })
      .catch(() => toast.error("Could not duplicate the article."));

  const logout = () =>
    axios
      .post(`${API}/auth/logout`, {}, { withCredentials: true })
      .finally(() => navigate("/admin/login", { replace: true }));

  return (
    <AdminGuard>
      <div className="min-h-screen bg-offwhite" data-testid="admin-dashboard">
        <header className="border-b border-navy/10 bg-white">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-4">
            <Link to="/" aria-label="English for Leaders — home">
              <Wordmark />
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/insights"
                data-testid="admin-view-site-link"
                className="inline-flex items-center gap-2 text-sm font-bold text-navy underline decoration-navy/30 underline-offset-4 hover:decoration-navy"
              >
                <Eye className="h-4 w-4" />
                View public hub
              </Link>
              <button
                type="button"
                data-testid="admin-logout-button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-sm border border-navy/20 px-4 py-2 text-sm font-bold text-navy transition-colors hover:border-navy"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-6 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-navy/60">
                Editorial Dashboard
              </p>
              <h1 className="mt-2 font-display text-3xl tracking-tight text-navy">
                Ideas, Questions &amp; Possibilities
              </h1>
            </div>
            <Link
              to="/admin/new"
              data-testid="admin-new-article-button"
              className="inline-flex items-center gap-2 rounded-sm bg-crimson px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-crimson-dark"
            >
              <Plus className="h-4 w-4" />
              New Article
            </Link>
          </div>

          <div className="mt-8 space-y-4">
            {articles === null && (
              <p className="text-sm text-charcoal/50" data-testid="admin-loading-list">
                Loading articles…
              </p>
            )}
            {articles?.length === 0 && (
              <p className="text-sm text-charcoal/50">No articles yet.</p>
            )}
            {articles?.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-article-row-${a.slug}`}
                className="flex flex-col gap-4 border border-navy/10 bg-white p-5 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`rounded-sm border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${STATUS_STYLES[a.status]}`}
                      data-testid={`status-${a.slug}`}
                    >
                      {STATUS_LABELS[a.status]}
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-navy/50">
                      {a.category}
                    </span>
                  </div>
                  <h2 className="mt-2 truncate font-display text-lg text-navy">
                    {a.title}
                  </h2>
                  <p className="mt-1 text-xs text-charcoal/50">/insights/{a.slug}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/admin/edit/${a.id}`}
                    data-testid={`edit-${a.slug}`}
                    className="rounded-sm border border-navy/20 px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:border-navy"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    data-testid={`duplicate-${a.slug}`}
                    onClick={() => duplicate(a.id)}
                    className="rounded-sm border border-navy/20 px-3 py-1.5 text-xs font-bold text-navy transition-colors hover:border-navy"
                  >
                    Duplicate
                  </button>
                  {a.status !== "published" ? (
                    <ConfirmAction
                      label="Publish"
                      confirmLabel="Publish now"
                      testid={`publish-${a.slug}`}
                      onConfirm={() => act(a.id, "publish", "Article published.")}
                    />
                  ) : (
                    <ConfirmAction
                      label="Unpublish"
                      confirmLabel="Unpublish now"
                      testid={`unpublish-${a.slug}`}
                      onConfirm={() => act(a.id, "unpublish", "Article unpublished (back to draft).")}
                    />
                  )}
                  {a.status !== "archived" && (
                    <ConfirmAction
                      label="Archive"
                      confirmLabel="Archive now"
                      testid={`archive-${a.slug}`}
                      onConfirm={() => act(a.id, "archive", "Article archived.")}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs leading-relaxed text-charcoal/50">
            Saving an article never publishes it. Publishing and unpublishing always
            ask for confirmation. Published articles join the sitemap automatically;
            drafts, review and archived articles stay out of it.
          </p>
        </main>
      </div>
    </AdminGuard>
  );
};

export default Dashboard;
